import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Accordion } from 'react-bootstrap'
import styled from 'styled-components'
import { usePublicSettings } from '@src/hooks/queries/useSettings'
import { PrimaryButton, colors } from '@src/theme/sharedComponents'
import BenefitsSection from './BenefitsSection'

const PageContainer = styled.section`
  background: ${colors.background};
  padding: 4rem 2rem;
  width: 100%;
`

const Inner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`

const PageHeader = styled.div`
  margin-bottom: 2.5rem;
`

const Eyebrow = styled.div`
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${colors.accent};
  margin-bottom: 0.5rem;
`

const PageTitle = styled.h1`
  font-size: clamp(2.2rem, 5vw, 4rem);
  font-weight: 900;
  color: ${colors.textLightMode};
  letter-spacing: -0.02em;
  margin: 0 0 0.5rem;
  line-height: 1;
  font-family: 'Barlow Condensed', 'Impact', sans-serif;
  text-transform: uppercase;
`

const PageSubtitle = styled.p`
  font-size: 1rem;
  color: ${colors.textLightModeMuted};
  margin: 0;
  max-width: 640px;
`

const Divider = styled.div`
  height: 2px;
  background: ${colors.borderLight};
  margin: 3rem 0;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 2px;
    width: 48px;
    background: ${colors.accent};
  }
`

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 1px;
  background: ${colors.borderLight};
  border: 1px solid ${colors.borderLight};
  margin-bottom: 3rem;
`

const StatCard = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1.75rem 1.5rem;
  background: ${colors.background};
`

const StatNumber = styled.div`
  font-size: 3rem;
  font-weight: 900;
  color: ${colors.primary};
  line-height: 1;
  font-family: 'Barlow Condensed', 'Impact', sans-serif;
`

const StatDescription = styled.div`
  font-size: 0.82rem;
  color: ${colors.textLightModeMuted};
  margin-top: 0.2rem;
`

const FAQTitle = styled.h2`
  font-size: clamp(1.5rem, 3vw, 2.2rem);
  font-weight: 900;
  color: ${colors.textLightMode};
  letter-spacing: -0.01em;
  margin-bottom: 1.5rem;
  font-family: 'Barlow Condensed', 'Impact', sans-serif;
  text-transform: uppercase;
`

const StyledAccordion = styled(Accordion)`
  max-width: 800px;

  .accordion-item {
    border: none;
    border-bottom: 1px solid ${colors.borderLight};
    border-radius: 0 !important;
    margin-bottom: 0;
    background: ${colors.background};

    &:first-child { border-top: 1px solid ${colors.borderLight}; }
  }

  .accordion-button {
    font-weight: 700;
    font-size: 0.9rem;
    color: ${colors.textLightMode};
    background: ${colors.background};
    padding: 1rem 1.25rem;
    box-shadow: none;
    border-radius: 0 !important;
    letter-spacing: 0.01em;

    &:not(.collapsed) {
      background: color-mix(in srgb, var(--color-primary) 5%, ${colors.background});
      color: ${colors.primaryDark};
      border-left: 3px solid ${colors.accent};
    }

    &:focus { box-shadow: none; }

    &::after {
      background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%2300508f'%3e%3cpath fill-rule='evenodd' d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3e%3c/svg%3e");
    }
  }

  .accordion-body {
    padding: 1rem 1.25rem;
    color: ${colors.textLightModeMuted};
    line-height: 1.6;
    font-size: 0.9rem;

    a { color: ${colors.primary}; text-decoration: none; &:hover { text-decoration: underline; } }
  }
`

const CTAContainer = styled.div`
  margin: 0 0 0.5rem;
`

const ClosingText = styled.p`
  font-size: 0.95rem;
  color: ${colors.textLightModeMuted};
  margin-top: 1.5rem;
`

const AboutPage: React.FC = () => {
  const navigate = useNavigate()
  const { data: s } = usePublicSettings()

  const allStats = [
    { value: s?.about_stat_1_value, description: s?.about_stat_1_description, showPlus: s?.about_stat_1_show_plus ?? true },
    { value: s?.about_stat_2_value, description: s?.about_stat_2_description, showPlus: s?.about_stat_2_show_plus ?? true },
    { value: s?.about_stat_3_value, description: s?.about_stat_3_description, showPlus: s?.about_stat_3_show_plus ?? true },
  ].filter(stat => stat.value)

  const faq = s?.about_faq ?? []

  return (
    <>
    <PageContainer>
      <Inner>
        <PageHeader>
          {s?.about_page_eyebrow && <Eyebrow>{s.about_page_eyebrow}</Eyebrow>}
          <PageTitle>{s?.about_page_title}</PageTitle>
          {s?.about_lead_text && <PageSubtitle>{s.about_lead_text}</PageSubtitle>}
        </PageHeader>

        {allStats.length > 0 && (
          <StatsRow>
            {allStats.map((stat, i) => (
              <StatCard key={i}>
                <StatNumber>{stat.value}{stat.showPlus && '+'}</StatNumber>
                <StatDescription>{stat.description}</StatDescription>
              </StatCard>
            ))}
          </StatsRow>
        )}

        {faq.length > 0 && (
          <>
            <Divider />
            <FAQTitle>Frequently Asked Questions</FAQTitle>
            <StyledAccordion>
              {faq.map((item, i) => (
                <Accordion.Item eventKey={String(i)} key={i}>
                  <Accordion.Header>{item.question}</Accordion.Header>
                  <Accordion.Body dangerouslySetInnerHTML={{ __html: item.answer }} />
                </Accordion.Item>
              ))}
            </StyledAccordion>
          </>
        )}

        <Divider />

        <CTAContainer>
          <PrimaryButton onClick={() => navigate('/contact')}>
            <i className="bi bi-envelope" />
            Contact Us Today
          </PrimaryButton>
        </CTAContainer>

        {s?.about_closing_text && <ClosingText>{s.about_closing_text}</ClosingText>}
      </Inner>
    </PageContainer>

    {s?.about_show_benefits !== false && <BenefitsSection />}
    </>
  )
}

export default AboutPage
